import React from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, Divider, ListSubheader } from '@mui/material';

import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { AppMenu, AppMenuItem } from './menu/primitives';
import { useCardShows } from './card/useCardDensity';
import {
  CONTROL_TEXT, ERROR, ERROR_HOVER, SANS_STACK, SUCCESS, SUCCESS_HOVER,
} from './tokens';

// Offered when rejecting.
export const REJECT_REASONS = ['spam', 'low quality', 'duplicate'];

const LEAVE_MS = 240;

// The corner ⋮: an 18px glyph, 4px of padding, and air before the nearest pill.
const MENU_CLEARANCE = 30;

export const VERDICT_COLORS = {
  approve: { bg: SUCCESS, hover: SUCCESS_HOVER },
  reject: { bg: ERROR, hover: ERROR_HOVER },
};

export function verdictButtonSx(kind, compact = false) {
  const { bg, hover } = VERDICT_COLORS[kind];
  return {
    border: 'none',
    color: '#ffffff',
    background: bg,
    borderRadius: '999px',
    padding: compact ? '8px' : '8px 18px',
    fontSize: '0.875rem',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.02857em',
    fontFamily: SANS_STACK,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    '&:hover': { background: hover },
    '&:disabled': { opacity: 0.5, cursor: 'default' },
  };
}

/**
 * Owns the "decision animates the card out"; the real handler fires only once
 * the transition has played, so the card never disappears out from under the click.
 *
 * @param {(kind: 'approve'|'reject', reason: string|null) => void} onDecide
 */
export function useVerdictTransition(onDecide) {
  const [leaving, setLeaving] = React.useState(false);
  const timerRef = React.useRef(null);

  React.useEffect(() => () => clearTimeout(timerRef.current), []);

  const decide = React.useCallback((kind, reason = null) => {
    if (leaving) return;
    setLeaving(true);
    timerRef.current = setTimeout(() => onDecide(kind, reason), LEAVE_MS);
  }, [leaving, onDecide]);

  const sx = {
    transition: `opacity ${LEAVE_MS}ms ease, transform ${LEAVE_MS}ms ease`,
    opacity: leaving ? 0 : 1,
    transform: leaving ? 'scale(0.94)' : 'scale(1)',
    // A card mid-exit must not take a second verdict.
    pointerEvents: leaving ? 'none' : 'auto',
  };

  return { decide, leaving, sx };
}

// A verdict bar may sit at the foot or top, so the reason menu opens away from
// the edge it is nearest.
const REASON_PLACEMENTS = {
  above: {
    anchorOrigin: { vertical: 'top', horizontal: 'center' },
    transformOrigin: { vertical: 'bottom', horizontal: 'center' },
  },
  below: {
    anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
    transformOrigin: { vertical: 'top', horizontal: 'right' },
  },
};

/**
 * The reason menu that reject opens. Picking a reason is itself the
 * confirmation — there is no second dialog.
 *
 * `title` names the scope being rejected ("Reject as" / "Reject 5 as").
 */
export function RejectReasonMenu(props) {
  const { anchorEl, title, placement, onClose, onPick } = props;
  const pick = (reason) => {
    onClose();
    onPick(reason);
  };
  return (
    <AppMenu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      {...REASON_PLACEMENTS[placement]}
    >
      <ListSubheader sx={{ lineHeight: '32px', fontWeight: 600 }}>{title}</ListSubheader>
      <Divider />
      {REJECT_REASONS.map((reason) => (
        <AppMenuItem key={reason} onClick={() => pick(reason)} sx={{ textTransform: 'lowercase' }}>
          {reason}
        </AppMenuItem>
      ))}
      <AppMenuItem onClick={() => pick(null)}>No reason</AppMenuItem>
    </AppMenu>
  );
}

RejectReasonMenu.propTypes = {
  anchorEl: PropTypes.object,
  title: PropTypes.string,
  placement: PropTypes.oneOf(['above', 'below']),
  onClose: PropTypes.func.isRequired,
  onPick: PropTypes.func.isRequired,
};

RejectReasonMenu.defaultProps = { anchorEl: null, title: 'Reject as', placement: 'above' };

/**
 * Decision-first footer for pending entities: Approve / Reject with
 * everything else demoted behind the overflow menu.
 *
 * Entity-agnostic — items, authors, sites and lists all pass the same shape.
 * Pair with `useVerdictTransition` so decisions animate the card out.
 *
 * The pills go icon-only on a card too narrow for the labels, never off one.
 */
export default function VerdictBar(props) {
  const { onDecide, reasonPicker, menuOptions, disabled } = props;
  const compact = !useCardShows()('verdictLabels');

  const [reasonAnchor, setReasonAnchor] = React.useState(null);
  const [menuAnchor, setMenuAnchor] = React.useState(null);

  const handleReject = (event) => {
    if (!reasonPicker) {
      onDecide('reject', null);
      return;
    }
    setReasonAnchor(event.currentTarget);
  };

  const runMenuOption = (option) => {
    setMenuAnchor(null);
    if (option.action) option.action();
  };

  return (
    <Box sx={{
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '6px',
      width: '100%',
      // Both sides, so the out-of-flow ⋮ keeps its corner and the pills stay centred.
      padding: menuOptions.length > 0 ? `0 ${MENU_CLEARANCE}px` : 0,
    }}
    >
      <Box
        component="button"
        type="button"
        disabled={disabled}
        title="Approve"
        onClick={() => onDecide('approve', null)}
        sx={verdictButtonSx('approve', compact)}
      >
        <CheckIcon sx={{ fontSize: 17 }} />
        {!compact && 'Approve'}
      </Box>
      <Box
        component="button"
        type="button"
        disabled={disabled}
        title="Reject"
        onClick={handleReject}
        sx={verdictButtonSx('reject', compact)}
      >
        <CloseIcon sx={{ fontSize: 17 }} />
        {!compact && 'Reject'}
      </Box>
      {menuOptions.length > 0 && (
        <IconButton
          size="small"
          disabled={disabled}
          onClick={(event) => setMenuAnchor(event.currentTarget)}
          sx={{
            position: 'absolute',
            right: 0,
            padding: '4px',
            borderRadius: '6px',
            color: CONTROL_TEXT,
          }}
        >
          <MoreVertIcon sx={{ fontSize: 18 }} />
        </IconButton>
      )}

      <RejectReasonMenu
        anchorEl={reasonAnchor}
        onClose={() => setReasonAnchor(null)}
        onPick={(reason) => onDecide('reject', reason)}
      />

      <AppMenu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {menuOptions.map((option) => (
          <AppMenuItem
            key={option.id}
            disabled={Boolean(option.disabled)}
            onClick={() => runMenuOption(option)}
          >
            {option.label}
          </AppMenuItem>
        ))}
      </AppMenu>
    </Box>
  );
}

VerdictBar.propTypes = {
  onDecide: PropTypes.func.isRequired,
  reasonPicker: PropTypes.bool,
  menuOptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.string.isRequired,
    action: PropTypes.func,
  })),
  disabled: PropTypes.bool,
};

VerdictBar.defaultProps = {
  reasonPicker: true,
  menuOptions: [],
  disabled: false,
};
